import styled, { css } from 'styled-components'
import { cardisTheme } from '../../theme/cardisTheme'

const printStyles = css`
  @media print {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// Keep the standard Cardis sheet height while allowing long descriptions to
// extend the section instead of overflowing into the following page.
export const PageContainer = styled.div`
  min-height: 311mm;
  height: auto;
  margin: 0;
  padding: 0;
  position: relative;
  font-family: ${cardisTheme.fonts.body};
  box-sizing: border-box;
  background-color: ${cardisTheme.colors.background};
  display: flex;
  flex-direction: column;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    min-height: 297mm !important;
    height: auto !important;
    max-height: none !important;
    page-break-after: always !important;
    break-inside: auto !important;
    page-break-inside: auto !important;
    overflow: visible !important;
    background-color: ${cardisTheme.colors.background} !important;
    ${printStyles}
  }
`

// ContentArea with gap and z-index (section-specific)
export const ContentArea = styled.div`
  flex: 1;
  padding: 38px 0px 0px 0px;
  display: flex;
  flex-direction: column;
  gap: 13px;
  z-index: 1;
`

// Title - xlarge variant with different margin
export const Title = styled.h1`
  font-family: ${cardisTheme.fonts.body};
  font-size: 30px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};

  @media print {
    ${printStyles}
  }
`

export const SectionHeader = styled.h2`
  font-family: ${cardisTheme.fonts.body};
  font-size: 13px;
  font-weight: 400;
  color: ${cardisTheme.colors.primary};
  margin: 6px 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media print {
    font-size: 13px !important;
    font-weight: 400 !important;
    color: ${cardisTheme.colors.primary} !important;
    text-transform: uppercase !important;
    margin: 6px 0 10px 0 !important;
    ${printStyles}
  }
`

// Cadastral Data Table
export const CadastralTable = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  @media print {
    width: 100% !important;
    display: flex !important;
    flex-direction: column !important;
  }
`

export const CadastralRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 5px 0;
  border-bottom: 1px solid ${cardisTheme.colors.border};
  align-items: anchor-center;

  &:last-child {
    border-bottom: none;
  }

  @media print {
    display: flex !important;
    justify-content: space-between !important;
    padding: 5px 0 !important;
    border-bottom: 1px solid ${cardisTheme.colors.border} !important;
    align-items: anchor-center !important;
    ${printStyles}
  }
`

export const CadastralLabel = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 500;
  color: ${cardisTheme.colors.primary};
  flex: 0 0 40%;

  @media print {
    font-size: 12px !important;
    font-weight: 500 !important;
    color: ${cardisTheme.colors.primary} !important;
    ${printStyles}
  }
`

export const CadastralLabelSubtext = styled.span`
  display: block;
  font-size: 8px;
  font-weight: 400;
  color: ${cardisTheme.colors.textLight};
  margin-top: 2px;

  @media print {
    font-size: 8px !important;
    color: ${cardisTheme.colors.textLight} !important;
    ${printStyles}
  }
`

export const CadastralValue = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.textLight};
  text-align: right;
  flex: 0 0 55%;

  @media print {
    font-size: 10px !important;
    font-weight: 400 !important;
    color: ${cardisTheme.colors.textLight} !important;
    text-align: right !important;
    ${printStyles}
  }
`

// Blue Metrics Card - Full width
export const MetricsCard = styled.div`
  background-color: ${cardisTheme.colors.primary};
  border-radius: 0;
  padding: 26px 51px;
  margin-left: -51px;
  margin-right: -51px;
  margin-top: 20px;

  @media print {
    background-color: ${cardisTheme.colors.primary} !important;
    border-radius: 0 !important;
    padding: 26px 51px !important;
    margin-left: -51px !important;
    margin-right: -51px !important;
    margin-top: 20px !important;
    ${printStyles}
  }
`

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 13px 38px;

  @media print {
    display: grid !important;
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 13px 38px !important;
  }
`

export const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    gap: 2px !important;
  }
`

export const MetricLabel = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.white};
  opacity: 0.85;

  @media print {
    font-size: 10px !important;
    color: ${cardisTheme.colors.white} !important;
    opacity: 0.85 !important;
    ${printStyles}
  }
`

export const MetricValue = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 16px;
  font-weight: 300;
  color: ${cardisTheme.colors.white};

  @media print {
    ${printStyles}
  }
`

export const MetricUnit = styled.span`
  font-size: 16px;
  font-weight: 300;
  margin-left: 3px;
`

// Details Section
export const DetailsSection = styled.div`
  margin-top: 6px;
  z-index: 1;

  @media print {
    margin-top: 6px !important;
  }
`

export const DetailsSubheader = styled.h3`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 500;
  color: ${cardisTheme.colors.primary};
  margin: 0 0 10px 0;
  padding-bottom: 3px;

  @media print {
    ${printStyles}
  }
`

export const DescriptionText = styled.div`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 400;
  color: ${cardisTheme.colors.textGray};
  line-height: 1.3;
  margin: 0 0 20px 0;
  text-align: justify;
  overflow-wrap: anywhere;
  word-break: normal;

  p {
    orphans: 3;
    widows: 3;
  }

  @media print {
    break-inside: auto !important;
    page-break-inside: auto !important;
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
    padding-top: 38px;
    padding-bottom: 38px;
    margin-top: -38px;
    ${printStyles}
  }
`

export const LegalNote = styled.p`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.textLight};
  line-height: 1.6;
  margin: 0;

  @media print {
    font-size: 10px !important;
    color: ${cardisTheme.colors.textLight} !important;
    ${printStyles}
  }
`

// Footer - section-specific with flex-based positioning (not absolute)
export const FooterWrapper = styled.div`
  position: relative;
  margin-top: auto;
  width: 100%;

  @media print {
    position: relative !important;
    margin-top: auto !important;
    border: none !important;
  }
`

export const FooterPattern = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 160px;
  object-fit: cover;
  z-index: 0;

  @media print {
    position: absolute !important;
    bottom: 0 !important;
    left: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    height: 160px !important;
    object-fit: cover !important;
    overflow: hidden !important;
    border: none !important;
    ${printStyles}
  }
`

export const Footer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;

  @media print {
    border: none !important;
    padding-right: 20px !important;
  }
`

export const PageNumber = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 600;
  color: ${cardisTheme.colors.primary};

  @media print {
    font-size: 12px !important;
    font-weight: 600 !important;
    color: ${cardisTheme.colors.primary} !important;
    ${printStyles}
  }
`
